/**
 * The reason the server gave for refusing a request, or the caller's own
 * (translated) fallback.
 *
 * An axios failure is an `Error` whose `.message` is transport text such as
 * "Request failed with status code 400". Showing that to a family tells them
 * nothing. The useful sentence is in `response.data.message` (or `.error`),
 * so toasts and inline errors read it from there, and the fallback stands in
 * when the server said nothing. An `Error` that did not come from axios is a
 * sentence some code of ours wrote on purpose, so it is kept, unless it
 * is the transport text itself.
 */
export function serverMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: unknown }; isAxiosError?: boolean } | null | undefined;
  const data = e?.response?.data;
  if (data && typeof data === 'object') {
    const d = data as { message?: unknown; error?: unknown };
    for (const v of [d.message, d.error]) {
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  }
  const fromAxios = e?.isAxiosError === true || e?.response !== undefined;
  if (!fromAxios && err instanceof Error) {
    const m = err.message.trim();
    if (m && !/^Request failed with status code \d+$/i.test(m)) return m;
  }
  return fallback;
}

/** The HTTP status of an axios-shaped failure, if it has one. */
export function serverStatus(err: unknown): number | undefined {
  const s = (err as { response?: { status?: unknown } } | null | undefined)?.response?.status;
  return typeof s === 'number' ? s : undefined;
}

/** The machine-readable `code` the server attached to a refusal, if any. */
export function serverCode(err: unknown): string | undefined {
  const c = (err as { response?: { data?: { code?: unknown } } } | null | undefined)?.response?.data?.code;
  return typeof c === 'string' ? c : undefined;
}
